import { api, type ManualPage } from '../api';
import { errorCard, esc, html, loading } from '../ui';

/**
 * The manual, on a screen.
 *
 * The helper reads these pages to answer questions; this is the same text, for
 * a person who would rather read it than ask. Written second and it shows: the
 * pages existed for weeks as files in the repository, which made them
 * documentation only for whoever had a checkout.
 *
 * Rendered here rather than served as HTML from the API, so the pages stay
 * plain markdown in git where the staleness check can reason about them and a
 * diff is readable.
 */

const state: { pages: ManualPage[]; open: string | null } = { pages: [], open: null };

/**
 * Enough markdown for what these pages actually use.
 *
 * Not a library: the pages are ours, the subset is small and fixed, and a
 * renderer that only understands what we write cannot be surprised by what we
 * do not. Everything is escaped first and only these shapes put back, so a page
 * cannot introduce markup by being written carelessly.
 */
function md(text: string): string {
  const inline = (s: string) =>
    esc(s)
      .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
      .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<i>$2</i>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // A link to another page, or to a screen. Absolute in-application paths
      // only: these pages are written here, but the rule is worth stating in
      // the renderer rather than in a review comment — an external href in the
      // manual would be a link out of the application from inside its own help.
      .replace(/\[([^\]]+)\]\((\/[^)\s]*)\)/g, '<a href="$2">$1</a>');

  const out: string[] = [];
  const lines = text.split(/\r?\n/);
  let list: 'ol' | 'ul' | null = null;

  const closeList = () => {
    if (list) out.push(`</${list}>`);
    list = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();

    if (!line) {
      closeList();
      continue;
    }

    // A table: a header row, a divider of dashes, then rows until a blank line.
    if (line.startsWith('|') && /^\|[\s:|-]+\|$/.test((lines[i + 1] ?? '').trim())) {
      closeList();
      const cells = (row: string) =>
        row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

      const head = cells(line);
      i += 1;
      const body: string[][] = [];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('|')) {
        i += 1;
        body.push(cells(lines[i]));
      }

      out.push(
        `<div class="scrollx"><table class="tbl" style="min-width:0">` +
          `<thead><tr>${head.map((c) => `<th>${inline(c)}</th>`).join('')}</tr></thead><tbody>` +
          // An empty cell stays empty. Filling it with a dash would be right in
          // a table of data, where blank means "no value"; here blank means
          // "same as the row above", and a dash flatly contradicts that — the
          // release-basis table would read as three regimes with no name.
          body
            .map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`)
            .join('') +
          `</tbody></table></div>`,
      );
      continue;
    }

    // A quotation — which on these pages is always the application's own
    // wording for a refusal, and the reason the page is worth reading. Without
    // this the leading `>` fell into the paragraph branch, which stripped the
    // first one and left every continuation line's `>` stranded in the middle
    // of the sentence: `hours, cycles or > months`.
    if (line.startsWith('>')) {
      closeList();
      const quote = [line.replace(/^>\s?/, '')];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('>')) {
        i += 1;
        quote.push(lines[i].trim().replace(/^>\s?/, ''));
      }
      out.push(`<blockquote>${inline(quote.join(' ').trim())}</blockquote>`);
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      closeList();
      // The page title is already drawn as the screen's heading, so a leading
      // `#` would say it twice.
      const level = Math.min(heading[1].length + 1, 4);
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      continue;
    }

    const ordered = /^(\d+)[.)]\s+(.*)$/.exec(line);
    const bulleted = /^[-*•]\s+(.*)$/.exec(line);

    if (ordered || bulleted) {
      const want = ordered ? 'ol' : 'ul';
      if (list !== want) {
        closeList();
        out.push(`<${want}>`);
        list = want;
      }

      // An item wrapped onto a second line is still one item. Without this the
      // continuation fell through to the paragraph branch, which closed the
      // list, split the sentence mid-word and orphaned the `**` of whatever
      // emphasis straddled the break — visible as raw asterisks in the middle
      // of a page. The pages are hand-written and wrap at eighty characters,
      // so almost every list was hitting it.
      const item = [ordered ? ordered[2] : bulleted![1]];
      while (
        i + 1 < lines.length &&
        lines[i + 1].trim() &&
        !/^(#{1,4}\s|[-*•]\s|\d+[.)]\s|\|>)/.test(lines[i + 1].trim())
      ) {
        i += 1;
        item.push(lines[i].trim());
      }

      out.push(`<li>${inline(item.join(' '))}</li>`);
      continue;
    }

    // A paragraph runs until a blank line; markdown's single newlines are not
    // breaks, and treating them as breaks makes every wrapped sentence ragged.
    closeList();
    const para = [line];
    while (
      i + 1 < lines.length &&
      lines[i + 1].trim() &&
      !/^(#{1,4}\s|[-*•]\s|\d+[.)]\s|\|>)/.test(lines[i + 1].trim())
    ) {
      i += 1;
      para.push(lines[i].trim());
    }
    out.push(`<p>${inline(para.join(' '))}</p>`);
  }

  closeList();
  return out.join('');
}

function view(): string {
  const open = state.pages.find((p) => p.slug === state.open) ?? state.pages[0];

  return html`
    <div class="phead">
      <div>
        <h1>Manual</h1>
        <p>
          How FlyPack CAMO works, in the words the helper answers from — the same pages, read directly.
          It covers what is not on the screen in front of you: where a control is, why a rule exists,
          what a refusal means.
        </p>
      </div>
    </div>

    <div class="cols">
      <div class="card">
        <div class="card-b manual-body">
          ${open ? html`<h2 style="margin-bottom:10px">${esc(open.title)}</h2>${md(open.body)}` : ''}
        </div>
      </div>

      <div class="card">
        <div class="card-h"><h3>Pages</h3></div>
        <div class="rows">
          ${state.pages
            .map(
              (p) => html`<button class="row manual-nav ${p.slug === open?.slug ? 'on' : ''}"
                type="button" data-page="${esc(p.slug)}">
                <div class="bd"><div class="t">${esc(p.title)}</div></div>
              </button>`,
            )
            .join('')}
        </div>
        <div class="card-f">
          Written and kept in the repository under <span class="mono">docs/manual</span>. A page whose
          code has changed since it was last written fails the build, so what is here describes what the
          application does now.
        </div>
      </div>
    </div>
  `;
}

export async function renderManual(host: HTMLElement, slug?: string): Promise<void> {
  host.innerHTML = loading('Manual');

  try {
    if (!state.pages.length) state.pages = (await api.assistant.manual()).pages;
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'The manual could not be read.');
    return;
  }

  if (!state.pages.length) {
    host.innerHTML = errorCard(
      'There is no manual on this server. It is bundled by the build — run `node tools/bundle-manual.mjs` after building, or set MANUAL_DIR.',
    );
    return;
  }

  if (slug && state.pages.some((p) => p.slug === slug)) state.open = slug;
  state.open ??= state.pages[0].slug;

  const paint = () => {
    host.innerHTML = view();
    host.querySelectorAll<HTMLButtonElement>('[data-page]').forEach((b) =>
      b.addEventListener('click', () => {
        state.open = b.dataset.page!;
        // In the URL so a page can be linked to and survives a refresh — the
        // reason anybody sends somebody a manual page at all.
        history.replaceState({}, '', `/manual/${state.open}`);
        paint();
        host.scrollIntoView({ block: 'start' });
      }),
    );
  };

  paint();
}
